import { Component, OnInit, inject, signal, effect } from '@angular/core';
import { CommonModule, DatePipe, CurrencyPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ParkerStateService } from '../services/parker-state.service';
import { PublicParkingProcessService, PublicParkingProcessDto, ParkerLicenseDto, StayPaymentStatus } from '../../api/gen';
import { PlateComponent, normalizePlate, findPlateCountry, RegionSeparator, JjamesButtonComponent } from 'shared-lib';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface PlatePresentation {
  fullNumber: string;
  euBand: string;
  countryCode: string;
  region: string;
  regionSeparator: RegionSeparator;
}

@Component({
  selector: 'app-parking-activity',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    CurrencyPipe,
    TranslatePipe,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    PlateComponent,
    JjamesButtonComponent
  ],
  templateUrl: './parking-activity.component.html',
  styleUrls: ['./parking-activity.component.scss']
})
export class ParkingActivityComponent implements OnInit {
  readonly state = inject(ParkerStateService);
  readonly PaymentStatus = StayPaymentStatus;
  private readonly publicProcessService = inject(PublicParkingProcessService);

  readonly historyProcesses = signal<PublicParkingProcessDto[]>([]);
  readonly isHistoryLoading = signal<boolean>(true);

  constructor() {
    // Reactively fetch history whenever the parker's plates load or change
    effect(() => {
      const plates = this.state.myPlates();
      const isPlatesLoading = this.state.isPlatesLoading();

      if (isPlatesLoading) {
        this.isHistoryLoading.set(true);
      } else if (plates && plates.length > 0) {
        this.loadHistoryForPlates(plates);
      } else {
        this.historyProcesses.set([]);
        this.isHistoryLoading.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.state.refreshPayableStays();
    if (this.state.myPlates().length === 0 && !this.state.isPlatesLoading()) {
      this.state.refreshAll();
    }
  }

  private loadHistoryForPlates(plates: ParkerLicenseDto[]): void {
    this.isHistoryLoading.set(true);
    const requests = plates.map(p => {
      const plateNumber = normalizePlate(p.licensePlate?.fullNumber);
      if (!plateNumber) return of(null);
      return this.publicProcessService.parkingProcessPublicByLicensePlatePost({
        licensePlateNumber: plateNumber,
        pageSize: 20,
        pageIndex: 0
      }).pipe(catchError(() => of(null)));
    });

    forkJoin(requests).subscribe({
      next: (results) => {
        const all: PublicParkingProcessDto[] = [];
        results.forEach(res => {
          if (res && res.items) {
            all.push(...res.items);
          }
        });
        // Sort newest first
        all.sort((a, b) => {
          const dateA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          const dateB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          return dateB - dateA;
        });
        this.historyProcesses.set(all);
        this.isHistoryLoading.set(false);
      },
      error: () => {
        this.isHistoryLoading.set(false);
      }
    });
  }

  getPlatePresentation(plateNumber?: string | null): PlatePresentation {
    const norm = normalizePlate(plateNumber);
    const country = 'AT';
    return {
      fullNumber: norm,
      countryCode: country,
      euBand: findPlateCountry(country)?.euBand ?? country,
      region: '',
      regionSeparator: findPlateCountry(country)?.regionSeparator ?? 'none'
    };
  }

  getDuration(startedAt?: string | null, endedAt?: string | null): string {
    if (!startedAt) return '—';
    const start = new Date(startedAt).getTime();
    const end = endedAt ? new Date(endedAt).getTime() : Date.now();
    const diffMs = Math.max(0, end - start);

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  // Covered is never shown as paid: a Dauerparker or Easypass holder was not charged anything.
  statusKey(status?: StayPaymentStatus): string {
    switch (status) {
      case StayPaymentStatus.Due: return 'ACTIVITY.STATUS_DUE';
      case StayPaymentStatus.Paid: return 'ACTIVITY.STATUS_PAID';
      case StayPaymentStatus.CoveredByRight: return 'ACTIVITY.STATUS_COVERED';
      default: return 'ACTIVITY.STATUS_NOTHING_TO_PAY';
    }
  }

  statusIcon(status?: StayPaymentStatus): string {
    switch (status) {
      case StayPaymentStatus.Due: return 'payments';
      case StayPaymentStatus.Paid: return 'check_circle';
      case StayPaymentStatus.CoveredByRight: return 'verified';
      default: return 'remove_circle_outline';
    }
  }

  statusClass(status?: StayPaymentStatus): string {
    switch (status) {
      case StayPaymentStatus.Due: return 'due';
      case StayPaymentStatus.Paid: return 'paid';
      case StayPaymentStatus.CoveredByRight: return 'covered';
      default: return 'neutral';
    }
  }

  onPayStay(parkingProcessId?: string | null): void {
    this.state.payStay(parkingProcessId);
  }
}
